import { useEffect } from "react";
import { trapFocus } from "../../../shared/utils";
import { checkIsStartOverSupported, checkIsLookbackSupported } from "../../../shared/utils/epg";
import { isPastProgram } from "../../../shared/utils";

export const useSOCUInTransition = (
  prevLiveProgramDetails,
  liveProgramDetails,
  liveStreamTransitionInProgress,
  haltTransitionSOCU
) => {
  useEffect(() => {
    if (
      prevLiveProgramDetails !== null &&
      (prevLiveProgramDetails.isLookback || (prevLiveProgramDetails.isRestarted && !haltTransitionSOCU.current)) &&
      liveProgramDetails.id !== prevLiveProgramDetails.prevLiveProgram.id
    ) {
      liveStreamTransitionInProgress.current = true;
    }
  }, [liveProgramDetails, liveStreamTransitionInProgress, prevLiveProgramDetails, haltTransitionSOCU]);
};

export const useSOCUTransationComplete = (
  liveProgramDetails,
  liveStreamTransitionInProgress,
  handleAVSStopContent,
  currentContentId,
  checkIsStartOverSupported,
  isInHome,
  transitionProgram,
  startOverStream,
  setStartTime,
  setEndTime,
  videoWrapper,
  handleNextLiveProgramStart,
  haltTransitionSOCU
) => {
  useEffect(() => {
    if (liveProgramDetails) {
      if (liveStreamTransitionInProgress.current) {
        if (isPastProgram(liveProgramDetails.metadata?.airingEndTime)) {
          handleAVSStopContent(null, currentContentId);
        } else {
          const isNextProgramRestartable = checkIsStartOverSupported(
            liveProgramDetails.metadata,
            liveProgramDetails.channel,
            isInHome
          );
          transitionProgram(isNextProgramRestartable);
          if (isNextProgramRestartable) {
            startOverStream(true);
          }
        }
        liveStreamTransitionInProgress.current = false;
      }
      handleNextLiveProgramStart();
      setStartTime(liveProgramDetails.metadata?.airingStartTime);
      setEndTime(liveProgramDetails.metadata?.airingEndTime);
    }
    trapFocus(videoWrapper.current);
    // TODO: Review and fix the dependency list later, current issue: if we include the full list dependency, a constant re-rendering is detected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveProgramDetails, liveStreamTransitionInProgress, currentContentId]);
};

export const useCheckSocuSupportEffect = (
  liveProgramDetails,
  isRestartLiveTVEnabled,
  isInHome,
  isLookbackEnabled,
  isLookbackStream,
  isCatchUpAllowed,
  isStartOverAllowed,
  isRestartTrickPlayAllowed
) => {
  useEffect(() => {
    if (liveProgramDetails && (isRestartLiveTVEnabled || isLookbackEnabled)) {
      if (isLookbackStream) {
        if (checkIsLookbackSupported(liveProgramDetails.metadata, liveProgramDetails.channel, isInHome)) {
          isCatchUpAllowed.current = true;
          if (liveProgramDetails.channel?.metadata?.isSOTVTrickplayEnabled) {
            isRestartTrickPlayAllowed.current = true;
          }
        }
      } else if (checkIsStartOverSupported(liveProgramDetails.metadata, liveProgramDetails.channel, isInHome)) {
        isStartOverAllowed.current = true;
        if (liveProgramDetails.channel?.metadata?.isSOTVTrickplayEnabled) {
          isRestartTrickPlayAllowed.current = true;
        }
      } else {
        isStartOverAllowed.current = false;
        isCatchUpAllowed.current = false;
        isRestartTrickPlayAllowed.current = false;
      }
    }
  }, [
    liveProgramDetails,
    isRestartLiveTVEnabled,
    isInHome,
    isLookbackEnabled,
    isLookbackStream,
    isCatchUpAllowed,
    isStartOverAllowed,
    isRestartTrickPlayAllowed,
  ]);
};

/**
 * make stop content call when exiting lookback mode,
 * to close the current lookback stream and avoid concurrent stream errors
 */
export const useExitLookbackModeEffect = (exitLookbackMode, hasCalledStopContent, handleAVSStopContent) => {
  useEffect(() => {
    if (exitLookbackMode) {
      if (hasCalledStopContent.current) hasCalledStopContent.current = false;
      handleAVSStopContent();
    }
  }, [exitLookbackMode, hasCalledStopContent, handleAVSStopContent]);
};
